import type { ContentRepository } from "../content/repository";
import { CONTENT_SCHEMA_VERSION } from "../content/types";
import type { ContentSchemaVersion, LevelId } from "../content/types";
import { SharedObject } from "../runtime";

/** Per-level hidden collectible: 0 none, 1 weapon chip, 2 armour chip. */
export interface ItemSlot {
  id: number;
  /** 0 not found, 1 picked up this run, 2 banked. */
  found: number;
}

export interface SaveState {
  schemaVersion: ContentSchemaVersion;
  unlockedLevels: LevelId[];
  /** Levels whose win condition has been met at least once. */
  completedLevels: LevelId[];
  acknowledgedLevels: LevelId[];
  highScores: Record<LevelId, number>;
  itemData: Record<LevelId, ItemSlot>;
  itemFound: { weaponChip: number; armourChip: number };
  /** Per weapon: 0 locked, 1 just unlocked, 2 owned. */
  weaponData: number[];
  /** Story milestone gate, drives the unlock popups on the title screen. */
  savedProgress: number;
  /** 1 all on, 2 music off, 3 sfx off, 4 all off. */
  soundState: number;
  hyperArmour: boolean;
  /** Player-facing cheat toggle; only honoured while the cheats menu is unlocked. */
  cheatsOn: boolean;
}

interface LegacySaveState {
  levelsUnlocked: number;
  levelsUnlockedMenu: number;
  highScores: number[];
  itemData: ItemSlot[];
  itemFound: { weaponChip: number; armourChip: number };
  weaponData: number[];
  savedProgress: number;
  soundState: number;
  hyperArmour: boolean;
}

const SLOT = "XenosMG";

/** Save slot backed by the Flash-style `SharedObject` shim (localStorage). */
export class SaveFile {
  private readonly so = SharedObject.getLocal(SLOT);

  constructor(private readonly content: ContentRepository) {}

  get isEmpty(): boolean {
    return this.so.size === 0;
  }

  read(): SaveState {
    const data = this.so.data as Record<string, unknown>;
    try {
      if (data.schemaVersion === CONTENT_SCHEMA_VERSION) {
        return normalizeCurrentSave(this.content, data as unknown as SaveState);
      }
      const migrated = migrateLegacySave(
        this.content,
        data as unknown as LegacySaveState,
      );
      this.write(migrated);
      return migrated;
    } catch {
      // a malformed local slot must not block the game from booting
      const fresh = newSaveState(this.content);
      this.write(fresh);
      return fresh;
    }
  }

  write(state: SaveState): void {
    for (const key of Object.keys(this.so.data)) {
      delete this.so.data[key];
    }
    Object.assign(this.so.data, copyCurrentSave(state));
    this.so.flush();
  }
}

/** A fresh game: authored levels start with no score or banked collectible. */
export function newSaveState(content: ContentRepository): SaveState {
  const highScores: Record<LevelId, number> = {};
  const itemData: Record<LevelId, ItemSlot> = {};
  for (const level of content.documents.levels) {
    highScores[level.id] = 0;
    itemData[level.id] = {
      id:
        level.hiddenCollectible === "weapon-chip"
          ? 1
          : level.hiddenCollectible === "armour-chip"
            ? 2
            : 0,
      found: 0,
    };
  }
  const unlockedLevels = [...content.initialLevelIds];
  return {
    schemaVersion: CONTENT_SCHEMA_VERSION,
    unlockedLevels,
    completedLevels: [],
    acknowledgedLevels: [...unlockedLevels],
    highScores,
    itemData,
    itemFound: { weaponChip: 0, armourChip: 0 },
    weaponData: [2, 2, 0, 0, 0],
    savedProgress: 0,
    soundState: 1,
    hyperArmour: false,
    cheatsOn: false,
  };
}

function copyCurrentSave(state: SaveState): SaveState {
  return {
    schemaVersion: CONTENT_SCHEMA_VERSION,
    unlockedLevels: [...state.unlockedLevels],
    completedLevels: [...state.completedLevels],
    acknowledgedLevels: [...state.acknowledgedLevels],
    highScores: { ...state.highScores },
    itemData: Object.fromEntries(
      Object.entries(state.itemData).map(([id, slot]) => [id, { ...slot }]),
    ),
    itemFound: { ...state.itemFound },
    weaponData: [...state.weaponData],
    savedProgress: state.savedProgress,
    soundState: state.soundState,
    hyperArmour: state.hyperArmour,
    cheatsOn: state.cheatsOn,
  };
}
function normalizeCurrentSave(
  content: ContentRepository,
  saved: SaveState,
): SaveState {
  const state = newSaveState(content);
  const known = new Set(content.documents.levels.map((level) => level.id));
  state.unlockedLevels = uniqueLevelIds([
    ...state.unlockedLevels,
    ...saved.unlockedLevels.filter((id) => known.has(id)),
  ]);
  state.completedLevels = uniqueLevelIds(
    (saved.completedLevels ?? legacyCompletedIds(saved.highScores)).filter(
      (id) => known.has(id),
    ),
  );
  state.acknowledgedLevels = uniqueLevelIds([
    ...state.acknowledgedLevels,
    ...saved.acknowledgedLevels.filter((id) => known.has(id)),
  ]);
  for (const id of known) {
    state.highScores[id] = saved.highScores[id] ?? state.highScores[id];
    state.itemData[id] = copyLegacyItem(saved.itemData[id], state.itemData[id]);
  }
  state.itemFound = { ...saved.itemFound };
  state.weaponData = [...saved.weaponData];
  state.savedProgress = saved.savedProgress;
  state.soundState = saved.soundState;
  state.hyperArmour = saved.hyperArmour;
  state.cheatsOn = saved.cheatsOn ?? false;
  return state;
}

function uniqueLevelIds(ids: readonly LevelId[]): LevelId[] {
  return [...new Set(ids)];
}

/**
 * Slots written before completion was tracked carry no record of it, so a
 * non-zero high score stands in: every authored level awards points.
 */
function legacyCompletedIds(
  highScores: Record<LevelId, number> | undefined,
): LevelId[] {
  if (highScores == null) return [];
  return Object.keys(highScores).filter((id) => highScores[id] > 0);
}

function migrateLegacySave(
  content: ContentRepository,
  legacy: LegacySaveState,
): SaveState {
  const state = newSaveState(content);
  const extraLevelId = content.worldMap.milestoneUnlocks.find(
    (milestone) => milestone.unlockLevel != null,
  )?.unlockLevel;
  const campaignIds = content.worldMap.nodes
    .filter((node) => node.id !== extraLevelId)
    .map((node) => node.id);
  state.unlockedLevels = legacyUnlockedIds(
    campaignIds,
    legacy.levelsUnlocked,
    extraLevelId,
  );
  state.acknowledgedLevels = legacyUnlockedIds(
    campaignIds,
    legacy.levelsUnlockedMenu,
    extraLevelId,
  );

  for (let index = 0; index < campaignIds.length; index++) {
    const id = campaignIds[index];
    state.highScores[id] = legacy.highScores?.[index] ?? 0;
    state.itemData[id] = copyLegacyItem(
      legacy.itemData?.[index + 1],
      state.itemData[id],
    );
  }

  if (extraLevelId != null) {
    state.highScores[extraLevelId] =
      legacy.highScores?.[campaignIds.length] ?? state.highScores[extraLevelId];
    state.itemData[extraLevelId] = copyLegacyItem(
      legacy.itemData?.[campaignIds.length + 1],
      state.itemData[extraLevelId],
    );
  }
  const survival = content.survival;
  state.highScores[survival.id] =
    legacy.highScores?.[campaignIds.length + 1] ??
    state.highScores[survival.id];

  state.completedLevels = legacyCompletedIds(state.highScores);
  state.itemFound = { ...legacy.itemFound };
  state.weaponData = [...legacy.weaponData];
  state.savedProgress = legacy.savedProgress;
  state.soundState = legacy.soundState;
  state.hyperArmour = legacy.hyperArmour;
  return state;
}

function legacyUnlockedIds(
  campaignIds: readonly LevelId[],
  count: number,
  extraLevelId: LevelId | undefined,
): LevelId[] {
  const ids = campaignIds.slice(0, Math.min(count, campaignIds.length));
  if (count > campaignIds.length && extraLevelId != null) {
    ids.push(extraLevelId);
  }
  return ids;
}

function copyLegacyItem(
  slot: ItemSlot | undefined,
  fallback: ItemSlot,
): ItemSlot {
  return slot == null ? fallback : { id: slot.id, found: slot.found };
}
